import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import PreviewWall from './PreviewWall'
import HorseTrackModal, { useHorseTrack } from './HorseTrackModal'
import ToolTour, { useToolTour, InfoTip, TourMarker, MarkerKey, ProfitSketch } from './ToolTour'
import '../styles/app.css'

const BOTS = [
  { id: 'ev', label: 'EV Bot', accent: '#60a5fa', rgb: '96,165,250' },
  { id: 'bonus', label: 'Bonus Bot', accent: '#a78bfa', rgb: '167,139,250' },
]

const BOT_ICONS = {
  ev: (
    <>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </>
  ),
  bonus: (
    <>
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5C9.5 3 11 5 12 8c1-3 2.5-5 4.5-5a2.5 2.5 0 0 1 0 5" />
    </>
  ),
}

// Near-black base with translucent glass surfaces, matching the tracker and
// the shared bc-header band
const C = {
  page: '#010714',
  card: 'rgba(255,255,255,0.02)',
  cardRaised: 'rgba(255,255,255,0.04)',
  glass: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 24px rgba(0,0,0,0.35)',
  border: '#1b2538',
  borderStrong: '#26354f',
  text: '#f1f5f9',
  muted: '#94a3b8',
  dim: '#64748b',
  blue: '#60a5fa',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#f87171',
}

const RESULT_STYLES = {
  WON: { label: 'Won', color: C.green, bg: 'rgba(16,185,129,0.10)', bd: 'rgba(16,185,129,0.25)' },
  PLACED: { label: 'Placed', color: C.amber, bg: 'rgba(245,158,11,0.10)', bd: 'rgba(245,158,11,0.25)' },
  LOST: { label: 'Lost', color: C.red, bg: 'rgba(239,68,68,0.10)', bd: 'rgba(239,68,68,0.25)' },
  PENDING: { label: 'Running', color: C.blue, bg: 'rgba(59,130,246,0.10)', bd: 'rgba(59,130,246,0.25)' },
  SKIP: { label: 'Skip', color: C.muted, bg: 'rgba(100,116,139,0.10)', bd: 'rgba(100,116,139,0.20)' },
}

// Shown blurred behind the upgrade wall
const SAMPLE_TIPS = [
  { id: 's1', bot: 'ev', venue: 'Randwick', race_number: 4, runner_number: '7', horse_name: 'Golden Sovereign', silk_url: 'https://assets.sbstatic.com.au/silks/AAP/9b6411dd-4e48-4562-888d-9c572968c051/09c648bf-b27d-4886-8863-b8e4b8c9f439.png', win_odds: 4.6, ev_percentage: 14.2, units: 2, start_offset: 95 },
  { id: 's2', bot: 'ev', venue: 'Flemington', race_number: 3, runner_number: '2', horse_name: 'Northern Belle', silk_url: 'https://assets.sbstatic.com.au/silks/AAP/9b6411dd-4e48-4562-888d-9c572968c051/28e9312c-3a3d-4080-9ccb-b7c140ea5a98.png', win_odds: 6.5, ev_percentage: 9.8, units: 1, bet_result: 'WON', finishing_position: 1, profit_loss_units: 5.5, start_offset: -1800 },
  { id: 's3', bot: 'ev', venue: 'Rosehill', race_number: 2, runner_number: '11', horse_name: 'Ocean Drive', silk_url: 'https://assets.sbstatic.com.au/silks/AAP/9b6411dd-4e48-4562-888d-9c572968c051/2ba3216b-a510-472e-affd-3137aaf475c3.png', win_odds: 3.8, ev_percentage: 11.5, units: 2, bet_result: 'PLACED', finishing_position: 3, profit_loss_units: -0.6, start_offset: -3600 },
  { id: 's4', bot: 'ev', venue: 'Doomben', race_number: 1, is_skip: true, start_offset: -5400 },
]

// Tips stay in Up next this long past the scheduled jump, since races often go off late
const JUMP_GRACE_MS = 5 * 60 * 1000

const sydneyDate = (d) => new Date(d).toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' })
const jumpTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

const countdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`
  return `${m}:${String(total % 60).padStart(2, '0')}`
}

const ordinal = (n) => {
  const v = n % 100
  const suffixes = ['th', 'st', 'nd', 'rd']
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || 'th'}`
}

// Bonus bot results assume a $50 bonus bet run with no lay: a winner returns
// the stake times (odds - 1) in cash, anything else returns nothing.
const BONUS_STAKE = 50
const bonusCash = (t) => (t.bet_result === 'WON' && t.win_odds ? BONUS_STAKE * (Number(t.win_odds) - 1) : 0)
const money = (v) => `$${Math.round(v).toLocaleString()}`

const signedUnits = (u) => `${u > 0 ? '+' : ''}${Number(u).toFixed(2)}u`
const odds = (o) => (o ? `$${Number(o).toFixed(2)}` : '–')
const STYLES = `
  @keyframes hrArrive { 0% { opacity: 0; transform: translateY(-8px) } 100% { opacity: 1; transform: none } }
  @keyframes hrGlow { 0% { box-shadow: 0 0 0 1px rgba(96,165,250,0.5), 0 12px 40px rgba(59,130,246,0.25) } 100% { box-shadow: 0 0 0 1px rgba(96,165,250,0), 0 12px 40px rgba(59,130,246,0) } }
  .hr-new { animation: hrArrive 0.35s ease-out, hrGlow 2.4s ease-out }
  .hr-row { transition: background-color 0.15s }
  .hr-row:hover { background-color: rgba(148,163,184,0.04) }
  .hr-bot-tab { transition: border-color 0.2s, background 0.2s, box-shadow 0.2s, transform 0.2s }
  .hr-bot-tab:hover { transform: translateY(-1px) }
  .hr-bot-tab[aria-pressed="false"]:hover { border-color: ${C.borderStrong} }
  .hr-bar { transition: width 1s linear }
  .hr-track-btn { transition: background-color 0.15s, border-color 0.15s }
  .hr-track-btn:hover { background-color: rgba(59,130,246,0.14) !important; border-color: rgba(59,130,246,0.5) !important }
  @keyframes hrSpin { to { transform: rotate(360deg) } }
  .hr-spin { animation: hrSpin 0.6s linear }
  .hr-icon-btn { transition: color 0.15s }
  .hr-icon-btn:hover { color: ${C.text} }
  @media (prefers-reduced-motion: reduce) { .hr-new, .hr-spin { animation: none } .hr-bar { transition: none } }
`

function NumberBadge({ number, size = 40 }) {
  return (
    <div style={{
      width: size, height: size, flexShrink: 0, borderRadius: size * 0.28,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(145deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
      border: `1px solid ${C.borderStrong}`,
      fontSize: size * 0.4, fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums',
    }}>
      {number}
    </div>
  )
}

// Jockey silk from Sportsbet's racecard, with the runner number tagged on.
// Falls back to the plain number badge if there is no silk or it fails to load.
function RunnerBadge({ tip, size = 40 }) {
  const [failed, setFailed] = React.useState(false)
  if (!tip.silk_url || failed) return <NumberBadge number={tip.runner_number} size={size} />
  const tag = Math.round(size * 0.44)
  return (
    <div style={{
      position: 'relative', width: size, height: size, flexShrink: 0, borderRadius: size * 0.28,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(circle at 50% 35%, rgba(148,163,184,0.16) 0%, rgba(148,163,184,0.03) 75%)',
      border: `1px solid ${C.borderStrong}`,
    }}>
      <img src={tip.silk_url} alt="" onError={() => setFailed(true)} style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
      <span style={{
        position: 'absolute', right: -6, bottom: -6, minWidth: tag, height: tag, padding: '0 4px', boxSizing: 'border-box',
        borderRadius: tag * 0.32, backgroundColor: C.page, border: `1px solid ${C.borderStrong}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.max(10, Math.round(size * 0.24)), fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums',
      }}>
        {tip.runner_number}
      </span>
    </div>
  )
}

function TrackButton({ tracked, onClick, size = 28, className }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
    width: size, height: size, padding: 0, borderRadius: '8px', flexShrink: 0,
  }
  if (tracked) {
    return (
      <span title="Tracked" className={className} style={{ ...base, color: C.green, backgroundColor: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </span>
    )
  }
  return (
    <button className={`hr-track-btn${className ? ` ${className}` : ''}`} onClick={onClick} title="Track bet" aria-label="Track bet" style={{ ...base, cursor: 'pointer', color: C.text, backgroundColor: 'rgba(255,255,255,0.04)', border: `1px solid ${C.borderStrong}` }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
    </button>
  )
}

// For the page tour: `highlight` rings one part of the card and `markers` pins
// numbered badges on parts ({ timer, selection, edge, odds, track } -> number)
function UpNextCard({ tip, now, fresh, isMobile, track, highlight, markers = {} }) {
  const spot = (part) => (highlight === part ? 'tt-spot' : undefined)
  const mark = (part) => markers[part] != null && <TourMarker n={markers[part]} />
  const bot = BOTS.find(b => b.id === tip.bot) || BOTS[0]
  const startMs = new Date(tip.start_time).getTime()
  const remaining = startMs - now
  const windowMs = Math.max(startMs - new Date(tip.posted_at).getTime(), 60 * 1000)
  const fraction = Math.min(1, Math.max(0, remaining / windowMs))
  // Past the scheduled jump the timer counts on as a negative: the race may be held up
  const urgent = remaining < 30 * 1000
  const timer = remaining > 0 ? countdown(remaining) : `-${countdown(-remaining)}`
  const edge = tip.ev_percentage != null
    ? { value: `${Number(tip.ev_percentage).toFixed(1)}%`, label: 'EV' }
    : tip.retention_percentage != null
      ? { value: `${Number(tip.retention_percentage).toFixed(1)}%`, label: 'Retention' }
      : null

  return (
    <div className={fresh ? 'hr-new' : undefined} style={{
      position: 'relative', overflow: 'hidden', borderRadius: '18px',
      border: `1px solid ${C.border}`,
      background: `radial-gradient(120% 90% at 100% 0%, rgba(${bot.rgb},0.10) 0%, rgba(${bot.rgb},0) 55%), linear-gradient(180deg, ${C.cardRaised} 0%, ${C.card} 100%)`,
      boxShadow: C.glass, backdropFilter: 'blur(10px)',
    }}>
      <div aria-hidden style={{ position: 'absolute', top: 0, left: '18px', right: '18px', height: '1px', background: `linear-gradient(90deg, rgba(${bot.rgb},0) 0%, rgba(${bot.rgb},0.6) 50%, rgba(${bot.rgb},0) 100%)` }} />

      <div style={{ padding: isMobile ? '16px' : '18px 20px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', ...T.ellipsis }}>
            {tip.venue} <span style={{ color: '#475569' }}>·</span> R{tip.race_number} <span style={{ color: '#475569' }}>·</span> {jumpTime(tip.start_time)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span className={spot('timer')} style={{
            position: 'relative',
            flexShrink: 0, minWidth: '64px', textAlign: 'center', boxSizing: 'border-box',
            padding: '5px 10px', borderRadius: '999px',
            fontSize: '15px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            color: urgent ? C.amber : C.text,
            backgroundColor: urgent ? 'rgba(245,158,11,0.10)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${urgent ? 'rgba(245,158,11,0.3)' : C.border}`,
          }}>
            {timer}
            {mark('timer')}
          </span>
          {track && (
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <TrackButton size={30} tracked={track.isTracked(tip)} onClick={() => track.open(tip)} className={spot('track')} />
              {mark('track')}
            </span>
          )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginTop: '16px' }}>
          <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
            <RunnerBadge tip={tip} size={isMobile ? 52 : 60} />
            {mark('selection')}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: C.text, letterSpacing: '-0.01em', ...T.ellipsis }}>
              {tip.horse_name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
              {edge && (
                <span className={spot('edge')} style={{ position: 'relative', display: 'inline-flex', alignItems: 'baseline', gap: '5px', padding: '3px 9px', borderRadius: '6px', backgroundColor: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: C.green, fontVariantNumeric: 'tabular-nums' }}>{edge.value}</span>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(52,211,153,0.75)' }}>{edge.label}</span>
                  {mark('edge')}
                </span>
              )}
              {tip.units ? (
                <span style={{ padding: '3px 9px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, color: C.muted, backgroundColor: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}` }}>
                  {tip.units} {Number(tip.units) === 1 ? 'unit' : 'units'}
                </span>
              ) : null}
            </div>
          </div>

          <div className={spot('odds')} style={{
            position: 'relative',
            flexShrink: 0, textAlign: 'center', minWidth: isMobile ? '76px' : '92px',
            padding: isMobile ? '8px 10px' : '10px 14px', borderRadius: '14px',
            background: `linear-gradient(180deg, rgba(${bot.rgb},0.16) 0%, rgba(${bot.rgb},0.06) 100%)`,
            border: `1px solid rgba(${bot.rgb},0.35)`,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06), 0 6px 20px rgba(${bot.rgb},0.12)`,
          }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: bot.accent, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Win</div>
            <div style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: 800, color: C.text, lineHeight: 1.15, marginTop: '2px', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
              {odds(tip.win_odds)}
            </div>
            {mark('odds')}
          </div>
        </div>
      </div>

      <div style={{ height: '3px', backgroundColor: 'rgba(255,255,255,0.04)' }}>
        <div className="hr-bar" style={{ height: '100%', width: `${fraction * 100}%`, background: urgent ? C.amber : `linear-gradient(90deg, rgba(${bot.rgb},0.6), ${bot.accent})` }} />
      </div>
    </div>
  )
}

// Shared by the header and every row, skips included, so columns never drift
const TABLE_COLUMNS = '68px minmax(0, 1fr) 72px 80px 60px 92px 84px 28px'

const edgeValue = (t) => {
  const v = t.ev_percentage ?? t.retention_percentage
  return v != null ? `${Number(v).toFixed(1)}%` : '–'
}

// EV rows show units won/lost; bonus rows show the cash a $50 bonus bet returned
function resultValue(tip) {
  if (!tip.bet_result) return { text: '–', color: C.dim }
  if (tip.bot === 'bonus') {
    const cash = bonusCash(tip)
    return cash > 0 ? { text: `+${money(cash)}`, color: C.green } : { text: '$0', color: C.muted }
  }
  if (tip.profit_loss_units == null) return { text: '–', color: C.dim }
  const pl = Number(tip.profit_loss_units)
  return { text: signedUnits(pl), color: pl > 0 ? C.green : pl < 0 ? C.red : C.muted }
}

function ResultPill({ kind }) {
  const st = RESULT_STYLES[kind]
  return (
    <span style={{ display: 'inline-block', width: '76px', textAlign: 'center', boxSizing: 'border-box', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '4px 0', borderRadius: '6px', color: st.color, backgroundColor: st.bg, border: `1px solid ${st.bd}` }}>
      {st.label}
    </span>
  )
}

function EmptySilk({ size }) {
  return <div style={{ width: size, height: size, flexShrink: 0, borderRadius: size * 0.28, border: `1px dashed ${C.borderStrong}` }} />
}

const T = {
  primary: { fontSize: '14px', fontWeight: 600, color: C.text },
  secondary: { fontSize: '12px', fontWeight: 500, color: C.dim },
  num: { fontSize: '14px', fontWeight: 600, color: C.text, textAlign: 'right' },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
}

function ResultRow({ tip, isMobile, fresh, last, track }) {
  const skip = tip.is_skip
  const kind = skip ? 'SKIP' : tip.bet_result || 'PENDING'
  const value = resultValue(tip)
  const finish = tip.bet_result && tip.finishing_position ? ordinal(tip.finishing_position) : '–'
  const badge = skip ? <EmptySilk size={isMobile ? 40 : 36} /> : <RunnerBadge tip={tip} size={isMobile ? 40 : 36} />
  const name = skip ? 'No selection' : tip.horse_name
  const row = {
    borderBottom: last ? 'none' : `1px solid ${C.border}`,
    opacity: skip ? 0.5 : 1,
    fontVariantNumeric: 'tabular-nums',
  }

  if (isMobile) {
    return (
      <div className={`hr-row${fresh ? ' hr-new' : ''}`} style={{ ...row, display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' }}>
        {badge}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ ...T.primary, ...T.ellipsis }}>{name}</span>
            {track && !skip && track.isTracked(tip) && (
              <svg aria-label="Tracked" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>
            )}
          </div>
          <div style={{ ...T.secondary, ...T.ellipsis, marginTop: '3px' }}>
            {tip.venue} R{tip.race_number} · {jumpTime(tip.start_time)}{skip ? '' : ` · ${odds(tip.win_odds)}`}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px', flexShrink: 0 }}>
          <span style={{ ...T.num, color: value.color }}>{value.text}</span>
          <ResultPill kind={kind} />
        </div>
      </div>
    )
  }

  return (
    <div className={`hr-row${fresh ? ' hr-new' : ''}`} style={{ ...row, display: 'grid', gridTemplateColumns: TABLE_COLUMNS, alignItems: 'center', gap: '16px', padding: '0 20px', height: '60px' }}>
      <div style={{ fontSize: '13px', fontWeight: 500, color: C.muted }}>{jumpTime(tip.start_time)}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
        {badge}
        <div style={{ minWidth: 0 }}>
          <div style={{ ...T.primary, ...T.ellipsis }}>{name}</div>
          <div style={{ ...T.secondary, ...T.ellipsis, marginTop: '3px' }}>{tip.venue} R{tip.race_number}</div>
        </div>
      </div>
      <div style={T.num}>{skip ? '–' : odds(tip.win_odds)}</div>
      <div style={{ ...T.num, fontWeight: 500, color: C.muted }}>{skip ? '–' : edgeValue(tip)}</div>
      <div style={{ ...T.num, fontWeight: 500, color: C.muted }}>{finish}</div>
      <div style={{ display: 'flex', justifyContent: 'center' }}><ResultPill kind={kind} /></div>
      <div style={{ ...T.num, color: value.color }}>{value.text}</div>
      <div>{track && !skip && <TrackButton tracked={track.isTracked(tip)} onClick={() => track.open(tip)} />}</div>
    </div>
  )
}

function ResultsTable({ bot, tips, isMobile, freshIds, track }) {
  const head = { fontSize: '11px', fontWeight: 700, color: '#8a8f96', textTransform: 'uppercase', letterSpacing: '0.06em' }
  return (
    <div style={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden', boxShadow: C.glass, backdropFilter: 'blur(10px)' }}>
      {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: TABLE_COLUMNS, alignItems: 'center', gap: '16px', padding: '0 20px', height: '40px', borderBottom: `1px solid ${C.border}`, backgroundColor: 'rgba(255,255,255,0.015)' }}>
          <div style={head}>Jump</div>
          <div style={head}>Selection</div>
          <div style={{ ...head, textAlign: 'right' }}>Odds</div>
          <div style={{ ...head, textAlign: 'right' }}>{bot === 'ev' ? 'EV' : 'Retention'}</div>
          <div style={{ ...head, textAlign: 'right' }}>Finish</div>
          <div style={{ ...head, textAlign: 'center' }}>Result</div>
          <div style={{ ...head, textAlign: 'right' }}>{bot === 'ev' ? 'P/L' : 'Cash'}</div>
          <div />
        </div>
      )}
      {tips.map((t, i) => (
        <ResultRow key={t.id} tip={t} isMobile={isMobile} fresh={freshIds.has(t.id)} last={i === tips.length - 1} track={track} />
      ))}
    </div>
  )
}

function Record({ won, placed, lost }) {
  const part = (n, letter, color) => (
    <span style={{ color }}>
      {n}<span style={{ fontSize: '12px', fontWeight: 600, marginLeft: '2px', opacity: 0.7 }}>{letter}</span>
    </span>
  )
  return (
    <span style={{ display: 'inline-flex', gap: '10px' }}>
      {part(won, 'W', C.green)}
      {part(placed, 'P', C.amber)}
      {part(lost, 'L', C.red)}
    </span>
  )
}

function StatsStrip({ bot, tips, isMobile }) {
  const bets = tips.filter(t => !t.is_skip)
  const settled = bets.filter(t => t.bet_result)
  const won = settled.filter(t => t.bet_result === 'WON').length
  const placed = settled.filter(t => t.bet_result === 'PLACED').length
  const lost = settled.length - won - placed
  const signedColor = (v) => (v > 0 ? C.green : v < 0 ? C.red : C.text)

  let items
  if (bot === 'ev') {
    const pl = settled.reduce((sum, t) => sum + (Number(t.profit_loss_units) || 0), 0)
    const turnover = settled.reduce((sum, t) => sum + (Number(t.units) || 0), 0)
    const pot = turnover ? (pl / turnover) * 100 : null
    items = [
      { label: 'Opportunities today', value: bets.length },
      { label: 'Record', value: settled.length ? <Record won={won} placed={placed} lost={lost} /> : '–' },
      { label: 'Profit', value: settled.length ? signedUnits(pl) : '–', color: settled.length ? signedColor(pl) : null },
      { label: 'POT', value: pot != null ? `${pot > 0 ? '+' : ''}${pot.toFixed(1)}%` : '–', color: pot != null ? signedColor(pot) : null },
    ]
  } else {
    const cash = settled.reduce((sum, t) => sum + bonusCash(t), 0)
    const expected = settled.reduce((sum, t) => sum + (Number(t.retention_percentage) || 0) / 100 * BONUS_STAKE, 0)
    items = [
      { label: 'Opportunities today', value: bets.length },
      { label: 'Win rate', value: settled.length ? `${Math.round((won / settled.length) * 100)}%` : '–' },
      { label: 'Cash extracted', hint: `$${BONUS_STAKE} bonus bets`, value: settled.length ? money(cash) : '–', color: cash > 0 ? C.green : null },
      { label: 'Expected cash', hint: 'from retention', value: settled.length ? money(expected) : '–' },
    ]
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden', boxShadow: C.glass, backdropFilter: 'blur(10px)' }}>
      {items.map((it, i) => (
        <div key={it.label} style={{
          padding: isMobile ? '14px 16px' : '16px 20px',
          borderLeft: (isMobile ? i % 2 : i) ? `1px solid ${C.border}` : 'none',
          borderTop: isMobile && i >= 2 ? `1px solid ${C.border}` : 'none',
        }}>
          <div style={{ fontSize: '12px', color: C.dim, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {it.label}{it.hint && <span style={{ color: '#475569' }}> · {it.hint}</span>}
          </div>
          <div style={{ fontSize: isMobile ? '20px' : '22px', fontWeight: 700, color: it.color || C.text, marginTop: '4px', fontVariantNumeric: 'tabular-nums' }}>{it.value}</div>
        </div>
      ))}
    </div>
  )
}

function SectionTitle({ children, count, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '28px 0 12px' }}>
      <h2 style={{ fontSize: '15px', fontWeight: 700, color: C.text, margin: 0 }}>{children}</h2>
      {count > 0 && <span style={{ fontSize: '12px', fontWeight: 600, color: C.dim }}>{count}</span>}
      {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
    </div>
  )
}

function EmptyUpNext() {
  return (
    <div style={{ border: `1px dashed ${C.borderStrong}`, borderRadius: '16px', padding: '28px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '15px', fontWeight: 600, color: C.text }}>Waiting for the next race</div>
      <div style={{ fontSize: '13px', color: C.muted, marginTop: '6px', lineHeight: 1.6 }}>
        Opportunities land here 2 minutes before the jump. Keep this page open and they appear instantly.
      </div>
    </div>
  )
}

// Races the bots will tip today (horse_schedule), until each one's tip lands.
// One row per race, with a pill for every bot that has it selected.
// `sidebar`: the desktop right-hand column - pills under the venue, and an
// empty state instead of nothing, so the column never looks broken.
// `collapsible`: mobile / narrow desktop - one tappable line between Up next
// and the results (closed by default), so it never pushes the results down.
function ComingUp({ schedule, tips, now, isMobile, teaser, sidebar, collapsible }) {
  const [open, setOpen] = React.useState(false)
  // Minutes only - off-peak the page re-renders every 30s, not every second
  const inTime = (r) => {
    const mins = Math.max(1, Math.ceil((new Date(r.start_time).getTime() - now) / 60000))
    return `in ${mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`}`
  }
  const tipped = new Set(tips.map(t => `${t.bot}:${t.market_id}`))
  const races = {}
  schedule
    .filter(s => new Date(s.start_time).getTime() > now && !tipped.has(`${s.bot}:${s.market_id}`))
    .forEach(s => {
      const key = `${s.venue}:${s.race_number}:${s.start_time}`
      races[key] = races[key] || { ...s, bots: [] }
      races[key].bots.push(s.bot)
    })
  const rows = Object.values(races).sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
  if (!rows.length && !sidebar) return null

  const title = <SectionTitle count={rows.length}>Coming up today</SectionTitle>
  if (!rows.length) {
    return (
      <>
        {title}
        <div style={{ border: `1px dashed ${C.borderStrong}`, borderRadius: '16px', padding: '24px 20px', fontSize: '13px', color: C.muted, textAlign: 'center', lineHeight: 1.6 }}>
          No more races scheduled today.
        </div>
      </>
    )
  }

  if (collapsible) {
    const next = rows[0]
    return (
      <div style={{ marginTop: '20px', border: `1px solid ${C.border}`, borderRadius: '16px', background: C.card, boxShadow: C.glass, overflow: 'hidden' }}>
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
          background: 'none', border: 'none', color: C.text, cursor: 'pointer', textAlign: 'left', font: 'inherit',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 700 }}>
              Coming up today <span style={{ fontSize: '12px', fontWeight: 600, color: C.dim }}>{rows.length}</span>
            </div>
            <div style={{ fontSize: '12px', color: C.muted, marginTop: '3px', ...T.ellipsis }}>
              Next: {next.venue}{next.race_number != null && ` R${next.race_number}`} · {jumpTime(next.start_time)} · {inTime(next)}
            </div>
          </div>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {open && (
          <div style={{ borderTop: `1px solid ${C.border}` }}>
            {rows.map((r, i) => (
              <div key={`${r.venue}:${r.race_number}:${r.start_time}`} style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                borderTop: i ? `1px solid ${C.border}` : 'none',
              }}>
                <div style={{ width: '64px', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: C.text }}>{jumpTime(r.start_time)}</div>
                  <div style={{ fontSize: '12px', color: C.dim }}>{inTime(r)}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 600, color: C.text, ...T.ellipsis }}>
                  {r.venue}{r.race_number != null && <span style={{ color: C.muted, fontWeight: 500 }}> R{r.race_number}</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  {BOTS.filter(b => r.bots.includes(b.id)).map(b => (
                    <span key={b.id} style={{
                      fontSize: '11px', fontWeight: 600, color: b.accent, whiteSpace: 'nowrap',
                      padding: '3px 9px', borderRadius: '999px',
                      background: `rgba(${b.rgb},0.10)`, border: `1px solid rgba(${b.rgb},0.25)`,
                    }}>{b.label.replace(' Bot', '')}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {title}
      {teaser && (
        <div style={{ fontSize: '13px', color: C.muted, margin: '-4px 0 12px', lineHeight: 1.6 }}>
          The races our bots are watching today. Selections are posted 2 minutes before each jump.
        </div>
      )}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: '16px', background: C.card, boxShadow: C.glass, overflow: 'hidden' }}>
        {rows.map((r, i) => {
          const race = (
            <div style={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 600, color: C.text, ...T.ellipsis }}>
              {r.venue}{r.race_number != null && <span style={{ color: C.muted, fontWeight: 500 }}> R{r.race_number}</span>}
            </div>
          )
          const pills = (
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              {BOTS.filter(b => r.bots.includes(b.id)).map(b => (
                <span key={b.id} style={{
                  fontSize: '11px', fontWeight: 600, color: b.accent, whiteSpace: 'nowrap',
                  padding: '3px 9px', borderRadius: '999px',
                  background: `rgba(${b.rgb},0.10)`, border: `1px solid rgba(${b.rgb},0.25)`,
                }}>{b.label}</span>
              ))}
            </div>
          )
          const key = `${r.venue}:${r.race_number}:${r.start_time}`
          const divider = i ? `1px solid ${C.border}` : 'none'

          // Sidebar: two calm lines - race + jump time, then bots + countdown
          if (sidebar) {
            return (
              <div key={key} style={{ padding: '16px 20px', borderTop: divider }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                  {race}
                  <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{jumpTime(r.start_time)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '10px' }}>
                  {pills}
                  <div style={{ fontSize: '12px', color: C.dim, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{inTime(r)}</div>
                </div>
              </div>
            )
          }

          return (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: isMobile ? '12px 14px' : '12px 18px',
              borderTop: divider,
            }}>
              <div style={{ width: isMobile ? '64px' : '72px', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: C.text }}>{jumpTime(r.start_time)}</div>
                <div style={{ fontSize: '12px', color: C.dim }}>{inTime(r)}</div>
              </div>
              {race}
              {pills}
            </div>
          )
        })}
      </div>
    </>
  )
}

function Feed({ bot, tips, now, isMobile, freshIds, upNextAction, track, afterUpNext }) {
  const isPast = (t) => new Date(t.start_time).getTime() + JUMP_GRACE_MS <= now
  const upNext = tips
    .filter(t => !t.is_skip && !t.bet_result && !isPast(t))
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
  const today = tips
    .filter(t => !upNext.includes(t))
    .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))

  return (
    <>
      <StatsStrip bot={bot} tips={tips} isMobile={isMobile} />

      <SectionTitle count={upNext.length} right={upNextAction}>Up next</SectionTitle>
      {upNext.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: !isMobile && upNext.length > 1 ? '1fr 1fr' : '1fr', gap: '12px' }}>
          {upNext.map(t => <UpNextCard key={t.id} tip={t} now={now} fresh={freshIds.has(t.id)} isMobile={isMobile} track={track} />)}
        </div>
      ) : <EmptyUpNext />}

      {afterUpNext}

      {today.length > 0 && (
        <>
          <SectionTitle count={today.filter(t => !t.is_skip).length}>Today's results</SectionTitle>
          <ResultsTable bot={bot} tips={today} isMobile={isMobile} freshIds={freshIds} track={track} />
        </>
      )}
    </>
  )
}

function BotTabs({ bot, setBot, counts, isMobile }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '8px' : '12px' }}>
      {BOTS.map(b => {
        const active = bot === b.id
        return (
          <button
            key={b.id}
            className="hr-bot-tab"
            onClick={() => setBot(b.id)}
            aria-pressed={active}
            style={{
              display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '14px', textAlign: 'left',
              padding: isMobile ? '12px' : '14px 16px', borderRadius: '14px', cursor: 'pointer', fontFamily: 'inherit',
              border: `1px solid ${active ? `rgba(${b.rgb},0.45)` : C.border}`,
              background: active
                ? `linear-gradient(135deg, rgba(${b.rgb},0.16) 0%, rgba(${b.rgb},0.04) 100%)`
                : 'rgba(255,255,255,0.015)',
              boxShadow: active ? `0 8px 28px rgba(${b.rgb},0.14), inset 0 1px 0 rgba(255,255,255,0.05)` : 'none',
            }}
          >
            <div style={{
              width: isMobile ? '34px' : '40px', height: isMobile ? '34px' : '40px', flexShrink: 0, borderRadius: '11px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: active ? `rgba(${b.rgb},0.18)` : 'rgba(148,163,184,0.08)',
              color: active ? b.accent : C.dim,
            }}>
              <svg width={isMobile ? 17 : 19} height={isMobile ? 17 : 19} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {BOT_ICONS[b.id]}
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: active ? C.text : C.muted }}>{b.label}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, lineHeight: 1, color: active ? b.accent : C.dim, fontVariantNumeric: 'tabular-nums' }}>{counts[b.id]}</div>
              <div style={{ fontSize: '10px', fontWeight: 600, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>today</div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

// Realtime rejoins on its own; Reconnect (a page reload) only appears once it
// has been down long enough that waiting is unlikely to help.
function ConnectionControls({ status, stalled, onRefresh }) {
  const [spinning, setSpinning] = React.useState(false)
  const live = status === 'SUBSCRIBED'
  const broken = !live && (stalled || ['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED'].includes(status))

  const refresh = async () => {
    setSpinning(true)
    await Promise.all([onRefresh(), new Promise(r => setTimeout(r, 600))])
    setSpinning(false)
  }

  const state = live
    ? { label: 'Live', color: C.green, rgb: '52,211,153' }
    : broken
      ? { label: 'Reconnect', color: C.amber, rgb: '251,191,36' }
      : { label: 'Connecting', color: C.dim, rgb: '100,116,139' }

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'stretch', borderRadius: '999px', overflow: 'hidden',
      border: `1px solid rgba(${state.rgb},0.28)`, backgroundColor: `rgba(${state.rgb},0.07)`,
      backdropFilter: 'blur(8px)',
    }}>
      <button
        onClick={broken ? () => window.location.reload() : undefined}
        disabled={!broken}
        style={{ padding: '7px 14px', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700, letterSpacing: '0.02em', color: state.color, cursor: broken ? 'pointer' : 'default' }}
      >
        {state.label}
      </button>
      <span style={{ width: '1px', backgroundColor: `rgba(${state.rgb},0.22)` }} />
      <button
        className="hr-icon-btn"
        onClick={refresh}
        disabled={spinning}
        title="Refresh tips"
        aria-label="Refresh tips"
        style={{ padding: '0 12px', display: 'flex', alignItems: 'center', border: 'none', background: 'none', color: C.muted, cursor: 'pointer' }}
      >
        <svg className={spinning ? 'hr-spin' : undefined} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v6h-6" />
        </svg>
      </button>
    </div>
  )
}

// Same top bar as the other desktop tool pages (bc-header inside
// .tool-unified). Phones get the title from PageRouter's header instead.
function TopBar({ status, stalled, onRefresh, onInfo }) {
  const [spinning, setSpinning] = React.useState(false)
  const connected = status === 'SUBSCRIBED'
  const broken = !connected && (stalled || ['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED'].includes(status))

  const refresh = async () => {
    setSpinning(true)
    await Promise.all([onRefresh(), new Promise(r => setTimeout(r, 600))])
    setSpinning(false)
  }

  return (
    <div className="bc-header">
      <div className="bc-header-titlewrap">
        <div className="bc-header-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
            <path d="M0 464V316.9C0 208.5 68.3 111.8 170.5 75.6L340.2 15.5C361.6 7.9 384 23.8 384 46.4c0 11-5.5 21.2-14.6 27.3L336 96c48.1 0 91.2 29.8 108.1 74.9l48.6 129.5c11.8 31.4 4.1 66.8-19.6 90.5c-16 16-37.8 25.1-60.5 25.1h-3.4c-26.1 0-50.9-11.6-67.6-31.7l-32.3-38.7c-11.7 4.1-24.2 6.4-37.3 6.4l-.1 0 0 0c-6.3 0-12.5-.5-18.6-1.5c-3.6-.6-7.2-1.4-10.7-2.3l0 0c-28.9-7.8-53.1-26.8-67.8-52.2c-4.4-7.6-14.2-10.3-21.9-5.8s-10.3 14.2-5.8 21.9c24 41.5 68.3 70 119.3 71.9l47.2 70.8c4 6.1 6.2 13.2 6.2 20.4c0 20.3-16.5 36.8-36.8 36.8H48c-26.5 0-48-21.5-48-48zM328 224c13.3 0 24-10.7 24-24s-10.7-24-24-24s-24 10.7-24 24s10.7 24 24 24z" />
          </svg>
        </div>
        <div className="bc-header-textcol">
          <div className="bc-header-titlerow">
            <h1 className="bc-title">Horse Racing Bot</h1>
            <button className="bc-info-btn" onClick={onInfo} aria-label="How this works" data-tooltip="How this works">?</button>
            {onRefresh && (
              <span
                className={connected ? 'bc-live-dot' : 'bc-live-dot stale'}
                data-tooltip={connected ? 'Live: opportunities appear instantly' : broken ? 'Connection lost' : 'Connecting'}
              />
            )}
          </div>
        </div>
      </div>
      {onRefresh && (
        <div className="bc-header-actions">
          {broken && (
            <button className="bc-btn" onClick={() => window.location.reload()}>Reconnect</button>
          )}
          <button className="bc-btn bc-icon-btn" onClick={refresh} disabled={spinning} aria-label="Refresh tips">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={spinning ? 'bc-spin' : ''}>
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Page tour ────────────────────────────────────────────────────────────
// Built from the page's own components with sample data, so the walkthrough
// always matches the live UI.
function BotIntroCard({ bot, text, isMobile }) {
  const b = BOTS.find(x => x.id === bot)
  return (
    <div style={{
      display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: isMobile ? 'center' : 'flex-start', gap: isMobile ? '14px' : '16px',
      padding: isMobile ? '14px 16px' : '20px', borderRadius: '16px', boxSizing: 'border-box',
      border: `1px solid rgba(${b.rgb},0.35)`,
      background: `linear-gradient(160deg, rgba(${b.rgb},0.16) 0%, rgba(${b.rgb},0.03) 100%)`,
      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 30px rgba(${b.rgb},0.12)`,
    }}>
      <div style={{ width: '44px', height: '44px', flexShrink: 0, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `rgba(${b.rgb},0.18)`, color: b.accent }}>
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {BOT_ICONS[b.id]}
        </svg>
      </div>
      <div>
        <div style={{ fontSize: '16px', fontWeight: 800, color: C.text, letterSpacing: '-0.01em' }}>{b.label}</div>
        <div style={{ fontSize: '13px', color: C.muted, lineHeight: 1.55, marginTop: '4px' }}>{text}</div>
      </div>
    </div>
  )
}

// Same three outcome cards as the Education lesson on horse racing promos
function OutcomeCard({ emoji, title, color, border, text, isMobile }) {
  return (
    <div style={{
      display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: isMobile ? 'center' : 'flex-start', gap: isMobile ? '12px' : '8px',
      padding: isMobile ? '10px 14px' : '16px', borderRadius: '12px', boxSizing: 'border-box',
      backgroundColor: '#0f172a', border: `1px solid ${border}`,
    }}>
      <div style={{ fontSize: isMobile ? '20px' : '24px', lineHeight: 1, flexShrink: 0 }}>{emoji}</div>
      <div>
        <div style={{ fontSize: '13.5px', fontWeight: 700, color }}>{title}</div>
        <div style={{ fontSize: isMobile ? '12px' : '12.5px', color: C.muted, lineHeight: 1.5, marginTop: '4px' }}>{text}</div>
      </div>
    </div>
  )
}

// Unit sizing guidance from the Education lesson
function UnitSizeInfo() {
  return (
    <InfoTip label="How to set your unit size" title="Setting your unit size">
      Keep your unit at <strong style={{ color: C.text }}>1-2% of your bankroll</strong>, and stick to it until you're ready to increase it.
      <span style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', margin: '10px 0' }}>
        {[['$1,250', '$25'], ['$2,500', '$50'], ['$5,000', '$100']].map(([bankroll, unit]) => (
          <span key={bankroll} style={{ display: 'block', padding: '7px 8px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}` }}>
            <span style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: C.text }}>{bankroll}</span>
            <span style={{ display: 'block', fontSize: '11px' }}>unit <span style={{ color: C.green, fontWeight: 700 }}>{unit}</span></span>
          </span>
        ))}
      </span>
      New to it? Start with a unit of $25 or less.
    </InfoTip>
  )
}

function RetentionInfo() {
  return (
    <InfoTip label="What retention means" title="Bonus retention">
      The share of the bonus you keep on average. <strong style={{ color: C.text }}>80% retention</strong> on a $50 bonus is <span style={{ color: C.green, fontWeight: 700 }}>$40</span> back over time.
      <span style={{ display: 'block', marginTop: '8px' }}>With no lay, single results swing: some bets win big, others return nothing. It evens out over many bonuses.</span>
    </InfoTip>
  )
}

function horseTourSlides(isMobile) {
  const now = Date.now()
  const evTip = { id: 'tour-ev', bot: 'ev', venue: 'Randwick', race_number: 7, runner_number: '7', horse_name: 'Golden Sovereign', win_odds: 4.6, ev_percentage: 14.2, units: 2, silk_url: 'https://assets.sbstatic.com.au/silks/AAP/9b6411dd-4e48-4562-888d-9c572968c051/09c648bf-b27d-4886-8863-b8e4b8c9f439.png', start_time: new Date(now + 95 * 1000).toISOString(), posted_at: new Date(now - 25 * 1000).toISOString() }
  const bonusTip = { id: 'tour-bonus', bot: 'bonus', venue: 'Caulfield', race_number: 6, runner_number: '3', horse_name: 'Second Chance', win_odds: 9.5, retention_percentage: 82.1, silk_url: 'https://assets.sbstatic.com.au/silks/AAP/9b6411dd-4e48-4562-888d-9c572968c051/28e9312c-3a3d-4080-9ccb-b7c140ea5a98.png', start_time: new Date(now + 240 * 1000).toISOString(), posted_at: new Date(now - 10 * 1000).toISOString() }
  const track = { isTracked: () => false, open: () => {} }
  const frame = { width: '100%', maxWidth: isMobile ? 'none' : '460px' }

  return [
    {
      title: 'Two bots, two jobs',
      body: 'This strategy takes advantage of the "bonus back if 2nd or 3rd" promos that almost every Australian bookmaker runs. Here\'s how it works.',
      visual: (
        <div style={{ ...frame, maxWidth: isMobile ? 'none' : '520px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '10px' : '14px' }}>
          <BotIntroCard bot="ev" isMobile={isMobile} text="Finds horses with positive expected value, 2 minutes before the jump." />
          <BotIntroCard bot="bonus" isMobile={isMobile} text="Finds horses with high bonus retention, for turning over your bonus bets." />
        </div>
      ),
    },
    {
      title: 'Here\'s how it works',
      body: 'Back the EV Bot\'s pick with a bookie running the promo. One of three things happens, and over time the maths is in your favour.',
      visual: (
        <div style={{ ...frame, maxWidth: isMobile ? 'none' : '560px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '8px' : '12px' }}>
          <OutcomeCard isMobile={isMobile} emoji="🏆" title="Your horse WINS" color="#34d399" border="#065f46" text="You collect your winnings in cash. Nothing else to do." />
          <OutcomeCard isMobile={isMobile} emoji="🥈" title="2nd or 3rd place" color="#60a5fa" border="#1e40af" text="Your stake comes back as a bonus bet. Turn it into cash with the Bonus Bot." />
          <OutcomeCard isMobile={isMobile} emoji="❌" title="4th or worse" color="#f87171" border="#7f1d1d" text="You lose your stake. This is already factored into the EV." />
        </div>
      ),
    },
    {
      title: 'Using the EV Bot',
      body: (
        <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
          <li>Open the bookie running the bonus back 2nd or 3rd promo.</li>
          <li>Check their odds match the card, or better.</li>
          <li>Stake the units shown × your unit size <UnitSizeInfo />, before the jump.</li>
          <li>Tap <strong style={{ color: C.text }}>+</strong> to track it in your Tracker.</li>
        </ol>
      ),
      visual: (
        <div style={frame}>
          <UpNextCard tip={evTip} now={now} isMobile={isMobile} track={track} markers={{ timer: 1, selection: 2, edge: 3, odds: 4, track: 5 }} />
          <MarkerKey isMobile={isMobile} labels={['Time to jump', 'Selection', 'EV & units', 'Win odds', 'Track']} />
        </div>
      ),
    },
    {
      title: 'Using the Bonus Bot',
      body: (
        <>
          <p style={{ margin: '0 0 8px' }}>Got a bonus bet from an EV Bot placing, or one sitting in another account? Here's how to turn it into cash.</p>
          <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
            <li>Open the bookie holding your bonus bet.</li>
            <li>Check their odds match the card, or better.</li>
            <li>Place your whole bonus bet on the selection. No lay or hedge needed.</li>
            <li>Tap <strong style={{ color: C.text }}>+</strong> to track it. Tick Free bonus if it didn't come from a promo or qualifying bet.</li>
          </ol>
        </>
      ),
      visual: (
        <div style={frame}>
          <UpNextCard tip={bonusTip} now={now} isMobile={isMobile} track={track} markers={{ timer: 1, selection: 2, edge: 3, odds: 4, track: 5 }} />
          <MarkerKey isMobile={isMobile} labels={['Time to jump', 'Selection', <>Retention <RetentionInfo /></>, 'Win odds', 'Track']} />
        </div>
      ),
    },
    {
      title: 'You\'re ready to go',
      body: 'Opportunities flow in automatically as they\'re found. Stick with the method over the long run and watch your profits grow: it typically returns around 20% POT, that\'s $20 for every $100 staked.',
      visual: <ProfitSketch isMobile={isMobile} headline="~20% POT" sub="$20 for every $100 staked" />,
    },
  ]
}

// One `horse_tips` channel for the whole session; the page only adds and
// removes listeners. Tearing the channel down and recreating it races
// realtime-js: channel() hands back the one still closing, and removing the
// last channel disconnects the socket underneath the new subscription.
const tipsHub = { channel: null, status: null, listeners: new Set() }

function listenToTips(listener) {
  tipsHub.listeners.add(listener)
  if (tipsHub.status) listener({ type: 'status', status: tipsHub.status })

  if (!tipsHub.channel) {
    const emit = (msg) => tipsHub.listeners.forEach(l => l(msg))
    const setStatus = (status) => {
      tipsHub.status = status
      emit({ type: 'status', status })
    }
    const onRow = ({ payload }) => { if (payload?.record) emit({ type: 'tip', tip: payload.record }) }

    const channel = supabase.channel('horse_tips', { config: { private: true } })
    tipsHub.channel = channel
    channel
      .on('broadcast', { event: 'INSERT' }, onRow)
      .on('broadcast', { event: 'UPDATE' }, onRow)

    // After a dev hot reload this module is fresh but the client still holds
    // the joined channel, and subscribe() on it is a no-op
    if (channel.state === 'joined') setStatus('SUBSCRIBED')
    else supabase.realtime.setAuth().finally(() => channel.subscribe(setStatus))
  }

  return () => tipsHub.listeners.delete(listener)
}

export default function HorseRacingBot({ isVisible = true }) {
  const { hasAccess, hadTrial, loading: authLoading } = useAuth()
  const hasHorseAccess = hasAccess('horse_bot')
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 640)
  // Wide enough for the "Coming up" sidebar next to a full-width results table
  const [isWide, setIsWide] = React.useState(window.innerWidth >= 1280)
  const [bot, setBot] = React.useState('ev')
  const [tips, setTips] = React.useState([])
  const [schedule, setSchedule] = React.useState([])
  const [loaded, setLoaded] = React.useState(false)
  const [status, setStatus] = React.useState(null)
  const [stalled, setStalled] = React.useState(false)
  const [freshIds, setFreshIds] = React.useState(() => new Set())
  const track = useHorseTrack()
  const tour = useToolTour('horse_bot', { isVisible })
  const [now, setNow] = React.useState(Date.now())

  React.useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth <= 640)
      setIsWide(window.innerWidth >= 1280)
    }
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const fetchTips = React.useCallback(async () => {
    const since = new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString()
    const { data, error } = await supabase
      .from('horse_tips')
      .select('*')
      .gte('start_time', since)
      .order('start_time', { ascending: true })
    if (error) {
      console.error('Failed to load horse tips:', error)
      return
    }
    setTips(data || [])
    setLoaded(true)
  }, [])

  // "Coming up" list - readable without a plan (teaser), so loaded for
  // everyone. It only changes when the bot's selections change, so a reload
  // every few minutes (and when the tab comes back) is enough.
  React.useEffect(() => {
    const fetchSchedule = async () => {
      const { data, error } = await supabase
        .from('horse_schedule')
        .select('*')
        .gte('start_time', new Date().toISOString())
        .order('start_time', { ascending: true })
      if (error) console.error('Failed to load horse schedule:', error)
      else setSchedule(data || [])
    }
    const onVisible = () => { if (!document.hidden) fetchSchedule() }
    fetchSchedule()
    const id = setInterval(fetchSchedule, 3 * 60 * 1000)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  // Initial load, then live pushes. Refetch whenever the channel
  // (re)subscribes or the tab comes back, so a dropped connection never
  // leaves the list missing a tip.
  React.useEffect(() => {
    if (!hasHorseAccess) return

    const flash = (id) => {
      setFreshIds(prev => new Set(prev).add(id))
      setTimeout(() => setFreshIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      }), 2500)
    }

    const onMessage = (msg) => {
      if (msg.type === 'status') {
        setStatus(msg.status)
        if (msg.status === 'SUBSCRIBED') fetchTips()
      } else {
        setTips(prev => [...prev.filter(t => t.id !== msg.tip.id), msg.tip])
        flash(msg.tip.id)
      }
    }

    const onVisible = () => { if (!document.hidden) fetchTips() }

    const unlisten = listenToTips(onMessage)
    fetchTips()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      unlisten()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [hasHorseAccess, fetchTips])

  React.useEffect(() => {
    if (!hasHorseAccess || status === 'SUBSCRIBED') {
      setStalled(false)
      return
    }
    const id = setTimeout(() => setStalled(true), 10000)
    return () => clearTimeout(id)
  }, [hasHorseAccess, status])

  const todayKey = sydneyDate(now)
  const todaysTips = tips.filter(t => sydneyDate(t.start_time) === todayKey)
  const botTips = todaysTips.filter(t => t.bot === bot)
  const counts = Object.fromEntries(BOTS.map(b => [b.id, todaysTips.filter(t => t.bot === b.id && !t.is_skip).length]))

  // Countdowns only need a 1s tick while a tip is still waiting to jump
  const hasUpcoming = todaysTips.some(t => !t.is_skip && !t.bet_result && new Date(t.start_time).getTime() + JUMP_GRACE_MS > now)
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), hasUpcoming ? 1000 : 30000)
    return () => clearInterval(id)
  }, [hasUpcoming])

  const container = { width: '100%', minHeight: '100vh', boxSizing: 'border-box', backgroundColor: C.page, color: C.text, padding: isMobile ? '16px 16px 96px' : 0 }
  const inner = { maxWidth: '960px', margin: '0 auto', padding: isMobile ? 0 : '24px 24px 64px' }
  // Subscriber view on wide screens: feed on the left, "Coming up" on the right
  const sidebarLayout = isWide && !isMobile

  if (authLoading) return <div style={container} />

  const tourEl = (
    <ToolTour
      open={tour.open}
      onClose={tour.close}
      slides={horseTourSlides(isMobile)}
      eyebrow="Horse Racing Bot"
      isMobile={isMobile}
      finalAction={hasHorseAccess ? { label: 'Get into it' } : { label: 'Unlock with Promo Premium', onClick: () => { window.location.hash = '#/pricing' } }}
      learnMore={{ label: 'Learn more', onClick: () => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'education' })) }}
    />
  )

  const connection = isMobile ? <ConnectionControls status={status} stalled={stalled} onRefresh={fetchTips} /> : null

  if (!hasHorseAccess) {
    const sampleTips = SAMPLE_TIPS.map(t => ({
      ...t,
      start_time: new Date(now + t.start_offset * 1000).toISOString(),
      posted_at: new Date(now - 25 * 1000).toISOString(),
    }))
    return (
      <div className={isMobile ? undefined : 'bc-container tool-unified'} style={container}>
        <style>{STYLES}</style>
        {!isMobile && <TopBar onInfo={tour.openTour} />}
        <div style={inner}>
          <div style={{ position: 'relative' }}>
            <div aria-hidden style={{ filter: 'blur(5px)', opacity: 0.55, pointerEvents: 'none', userSelect: 'none', maxHeight: isMobile ? '420px' : '520px', overflow: 'hidden' }}>
              <Feed bot="ev" tips={sampleTips} now={now} isMobile={isMobile} freshIds={new Set()} />
            </div>
            <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(1,7,20,0) 0%, ${C.page} 85%)` }} />
          </div>
          <div style={{ maxWidth: '520px', margin: isMobile ? '-280px auto 0' : '-320px auto 0', position: 'relative' }}>
            <PreviewWall
              title="Live horse racing opportunities"
              features={['EV and Bonus bot selections 2 minutes before the jump', 'Opportunities appear the moment they are posted', 'Results and profit tracked through the day', 'Also delivered to Discord']}
              planName="Promo Premium"
              accent="#a78bfa"
              hadTrial={hadTrial}
            />
          </div>
          {/* Teaser: the real list of races the bots are watching, under the upgrade wall */}
          <ComingUp schedule={schedule} tips={[]} now={now} isMobile={isMobile} teaser />
        </div>
      {tourEl}
      </div>
    )
  }

  return (
    <div className={isMobile ? undefined : 'bc-container tool-unified'} style={container}>
      <style>{STYLES}</style>
      {!isMobile && <TopBar status={status} stalled={stalled} onRefresh={fetchTips} onInfo={tour.openTour} />}
      <div style={sidebarLayout ? { ...inner, maxWidth: '1360px', padding: '24px 40px 64px' } : inner}>
        <div style={sidebarLayout ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '40px', alignItems: 'start' } : undefined}>
          <div>
            <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
              <BotTabs bot={bot} setBot={setBot} counts={counts} isMobile={isMobile} />
            </div>
            {loaded
              ? <Feed bot={bot} tips={botTips} now={now} isMobile={isMobile} freshIds={freshIds} upNextAction={isMobile ? connection : null} track={track}
                  afterUpNext={!sidebarLayout && <ComingUp schedule={schedule} tips={tips} now={now} isMobile={isMobile} collapsible />} />
              : <div style={{ fontSize: '14px', color: C.dim, padding: '40px 0', textAlign: 'center' }}>Loading opportunities…</div>}
          </div>
          {sidebarLayout && (
            <aside style={{ position: 'sticky', top: '24px', marginTop: '-28px' }}>
              {loaded && <ComingUp schedule={schedule} tips={tips} now={now} isMobile={isMobile} sidebar />}
            </aside>
          )}
        </div>
      </div>
      <HorseTrackModal
        tip={track.tip}
        form={track.form}
        onFormChange={track.setForm}
        onConfirm={track.save}
        onClose={track.close}
        saving={track.saving}
        badge={track.tip && <RunnerBadge tip={track.tip} size={52} />}
      />
      {tourEl}
    </div>
  )
}
